import React, { useState } from "react";
import { TbSearch, TbCaretUpFilled } from "react-icons/tb";
import { useFeatureRequests } from "../../hooks/useFeatureRequests";
import { fmtDate } from "../../lib/dates";
import { Button, Field, Alert, EmptyState, Modal, TopbarSlot } from "../../components";
import "./Settings.css";

const STATUS_LABEL = {
  new: "New", in_progress: "In progress", completed: "Completed", rejected: "Rejected",
};
const VIEWS = [
  { value: "", label: "All", countKey: "all" },
  { value: "new", label: "New", countKey: "new" },
  { value: "in_progress", label: "In progress", countKey: "in_progress" },
  { value: "completed", label: "Completed", countKey: "completed" },
  { value: "rejected", label: "Rejected", countKey: "rejected" },
];
// Sort is a chip that cycles, as in the kit, rather than a select.
const SORTS = [
  { value: "most_votes", label: "Votes ↓" },
  { value: "newest", label: "Newest" },
  { value: "most_commented", label: "Most discussed" },
  { value: "priority", label: "Priority" },
];
const CATEGORIES = [
  { value: "functionality", label: "Functionality" },
  { value: "ui", label: "Interface" },
  { value: "reporting", label: "Reporting" },
  { value: "integration", label: "Integration" },
];
// Status drives the badge colour; everything else stays neutral so the one thing a
// user needs from the roadmap is the thing that stands out.
const STATUS_TONE = { in_progress: "accent", completed: "success", rejected: "danger" };
const PRIORITY_TONE = { critical: "danger", high: "warning" };
const labelFor = (list, v) => list.find((i) => i.value === v)?.label || v;

// Feature requests. Votes drive the order, which is the whole point of the board, so
// "most votes" is the default sort rather than newest.
const FeatureRequestsScreen = ({ me }) => {
  const { requests, loading, error, page, setPage, totalPages, statusCounts, filters, setFilters, vote, create } = useFeatureRequests();
  const [composing, setComposing] = useState(false);
  const [adding, setAdding] = useState(false);

  const set = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };
  const cycleSort = () => {
    const i = SORTS.findIndex((x) => x.value === filters.sort);
    set({ sort: SORTS[(i + 1) % SORTS.length].value });
  };

  return (
    <div className="v2-page v2-page--flush">
      <TopbarSlot>
        <div className="v2-omni">
          <TbSearch size={14} />
          <input type="search" value={filters.search} placeholder="Search requests"
                 aria-label="Search requests" onChange={(e) => set({ search: e.target.value })} />
        </div>
        <Button variant="primary" onClick={() => setComposing(true)}>New request</Button>
      </TopbarSlot>

      <div className="v2-views">
        {VIEWS.map((v) => (
          <button key={v.value || "all"} type="button"
                  className={`v2-view${filters.status === v.value ? " on" : ""}`}
                  aria-current={filters.status === v.value ? "page" : undefined}
                  onClick={() => set({ status: v.value })}>
            {v.label}
            {statusCounts[v.countKey] != null && <span className="n">{statusCounts[v.countKey]}</span>}
          </button>
        ))}
      </div>

      <div className="v2-filters">
        {filters.category ? (
          <button type="button" className="v2-chip on" onClick={() => set({ category: "" })}
                  aria-label={`Remove the ${labelFor(CATEGORIES, filters.category)} category filter`}>
            Category <b>{labelFor(CATEGORIES, filters.category)}</b> <span className="x">✕</span>
          </button>
        ) : adding ? (
          <select className="v2-chipselect" autoFocus defaultValue=""
                  onBlur={() => setAdding(false)}
                  onChange={(e) => { set({ category: e.target.value }); setAdding(false); }}>
            <option value="" disabled>Category…</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        ) : (
          <button type="button" className="v2-chip" onClick={() => setAdding(true)}>+ Add filter</button>
        )}
        <button type="button" className="v2-chip" onClick={cycleSort}>
          Sort <b>{labelFor(SORTS, filters.sort)}</b>
        </button>
      </div>

      <div className="v2-requests">
        <Alert>{error}</Alert>

        {loading ? <p className="v2-hint">Loading…</p>
          : requests.length === 0 ? (
            <EmptyState title="Nothing here yet"
              action={<Button variant="primary" style={{ marginTop: ".75rem" }} onClick={() => setComposing(true)}>New request</Button>}>
              {filters.search ? `Nothing matches "${filters.search}".` : "Be the first to ask for something."}
            </EmptyState>
          ) : requests.map((r) => {
            const comments = r.comment_count ?? r.commentCount ?? 0;
            return (
              <article key={r.id} className="v2-request">
                <button type="button" className="v2-vote"
                        onClick={() => vote(r.id, "up")} aria-label={`Upvote ${r.title}`}>
                  <TbCaretUpFilled size={12} />
                  <span>{r.votes ?? 0}</span>
                </button>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="v2-request-head">
                    <span className="v2-request-title">{r.title}</span>
                    {r.status && (
                      <span className={`v2-rbadge v2-rbadge--${STATUS_TONE[r.status] || "neutral"}`}>
                        {(STATUS_LABEL[r.status] || r.status).toUpperCase()}
                      </span>
                    )}
                    {r.category && <span className="v2-rbadge">{String(r.category).toUpperCase()}</span>}
                    {PRIORITY_TONE[r.priority] && (
                      <span className={`v2-rbadge v2-rbadge--${PRIORITY_TONE[r.priority]}`}>
                        {String(r.priority).toUpperCase()}
                      </span>
                    )}
                  </div>
                  {r.description && <p className="v2-request-desc">{r.description}</p>}
                  <div className="v2-request-meta">
                    <span>{r.submitter || "Someone"}</span>
                    <span>·</span>
                    <span>{fmtDate(r.created_at)}</span>
                    {comments > 0 && (
                      <>
                        <span>·</span>
                        <span className="v2-request-comments">{comments} {comments === 1 ? "comment" : "comments"}</span>
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}

        {totalPages > 1 && (
          <div className="v2-pager">
            <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
            <span className="v2-hint">Page {page} of {totalPages}</span>
            <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</Button>
          </div>
        )}
      </div>

      {composing && <ComposeModal onClose={() => setComposing(false)} onCreate={create} />}
    </div>
  );
};

const ComposeModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({ title: "", description: "", category: "functionality", priority: "medium" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.title.trim()) { setErr("Give the request a title."); return; }
    setBusy(true); setErr("");
    try { await onCreate(form); onClose(); }
    catch (e) { setErr(e.message || "Could not submit the request."); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="New feature request" onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Submitting…" : "Submit"}</Button>
      </>}>
      <Alert>{err}</Alert>
      <Field label="Title"><input className="v2-input" value={form.title} onChange={set("title")} autoFocus /></Field>
      <Field label="What would you like, and why?" hint="The why is what makes a request get built.">
        <textarea className="v2-input" rows={6} value={form.description} onChange={set("description")} />
      </Field>
      <Field label="Category">
        <select className="v2-input" value={form.category} onChange={set("category")}>
          <option value="functionality">Functionality</option>
          <option value="ui">Interface</option>
          <option value="reporting">Reporting</option>
          <option value="integration">Integration</option>
        </select>
      </Field>
    </Modal>
  );
};

export default FeatureRequestsScreen;
